import Image from "next/image"
import {
	Box,
	Divider,
	Grid,
	ToggleButton,
	ToggleButtonGroup,
	Tooltip,
	TooltipProps,
} from "@mui/material"
import { styled } from "@mui/material/styles"
import Icon from "@mdi/react"
import { mdiCheckCircle } from "@mdi/js"

import Price from "components/Price"

import {
	ColorsBySafeLockConfigurationProductSku,
	DispatchProductDetailPageReducer,
	ProductDetailPageReducerState,
} from "@secureo/common/reducers/productDetailPageReducer"

import useTranslation from "hooks/useTranslation"

import { makeStyles } from "makeStyles"
import setSafeVariant from "@secureo/common/reducers/productDetailPageReducer/actions/safes/setSafeVariant"
import { netToGross } from "@secureo/common/utils/tax"

const useStyles = makeStyles()({
	toggleButton: {
		borderRadius: 3,
		height: "80px",
		width: "80px",
		textAlign: "center",
		marginRight: "7px",
		marginBottom: "7px",
		color: "black",
		padding: 0,
	},
})

const StyledToggleButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
	"& .MuiToggleButtonGroup-grouped": {
		marginRight: theme.spacing(0.7),
		padding: theme.spacing(0, 1),
		"&:not(:first-of-type)": {
			borderRadius: theme.shape.borderRadius,
			border: "1px solid grey",
		},
		"&:first-of-type": {
			borderRadius: theme.shape.borderRadius,
			border: "1px solid grey",
		},
	},
}))

const LockToolTip = ({ name, description, image }: Lock) => (
	<div style={{ minWidth: "300px" }}>
		<Box component="div" py={1}>
			<strong>{name}</strong>
		</Box>
		<Grid container spacing={1}>
			<Grid item xs={12}>
				{description && (
					<>
						<Divider />
						<Box>
							<div dangerouslySetInnerHTML={{ __html: description }}></div>
						</Box>
					</>
				)}
			</Grid>
			<Grid item xs={12} style={{ textAlign: "center" }}>
				{image && (
					<Image
						alt={name}
						src={image}
						width={150}
						height={150}
						style={{
							maxWidth: "100%",
							height: "auto",
							objectFit: "contain",
						}}
					/>
				)}
			</Grid>
		</Grid>
	</div>
)

const arrowGenerator = (color: string) => {
	return {
		'&[x-placement*="bottom"] $arrow': {
			top: 0,
			left: 0,
			marginTop: "-0.95em",
			width: "2em",
			height: "1em",
			"&::before": {
				borderWidth: "0 1em 1em 1em",
				borderColor: `transparent transparent ${color} transparent`,
			},
		},
		'&[x-placement*="top"] $arrow': {
			bottom: 0,
			left: 0,
			marginBottom: "-0.95em",
			width: "2em",
			height: "1em",
			"&::before": {
				borderWidth: "1em 1em 0 1em",
				borderColor: `${color} transparent transparent transparent`,
			},
		},
		'&[x-placement*="right"] $arrow': {
			left: 0,
			marginLeft: "-0.95em",
			height: "2em",
			width: "1em",
			"&::before": {
				borderWidth: "1em 1em 1em 0",
				borderColor: `transparent ${color} transparent transparent`,
			},
		},
		'&[x-placement*="left"] $arrow': {
			right: 0,
			marginRight: "-0.95em",
			height: "2em",
			width: "1em",
			"&::before": {
				borderWidth: "1em 0 1em 1em",
				borderColor: `transparent transparent transparent ${color}`,
			},
		},
	}
}

const useStylesArrow = makeStyles()((theme) => ({
	tooltip: {
		position: "relative",
		backgroundColor: "white",
		color: "rgba(0, 0, 0, 0.87)",
		boxShadow: "0px 0px 5px 0px rgba(0,0,0,0.08)",
		fontSize: 14,
		border: "1px solid lightgrey",
	},
	popper: arrowGenerator(theme.palette.grey[700]),
}))

const ArrowTooltip = (props: TooltipProps) => {
	const {
		classes: { ...classes },
	} = useStylesArrow()

	return (
		<Tooltip enterDelay={150} classes={classes} arrow {...props} title={<>{props.title}</>} />
	)
}

const getVariantIdWithMatchingColorIfPossible = (
	colorsBySafeLockConfigurationProductSku: ColorsBySafeLockConfigurationProductSku,
	lockConfigurationProductSku: string,
	selectedColorHexCode: string,
) => {
	const variantsWithMatchingLock =
		colorsBySafeLockConfigurationProductSku[lockConfigurationProductSku] ?? []

	const variantWithMatchingColor = variantsWithMatchingLock.find(
		(variant) => variant.colorConfigurationHexCode === selectedColorHexCode,
	)

	if (variantWithMatchingColor) return variantWithMatchingColor?.variantId
	return variantsWithMatchingLock?.[0]?.variantId
}

const LockConfiguration = ({ reducerState, dispatch }: Props) => {
	const { classes } = useStyles()
	const { t } = useTranslation("product")

	const {
		variantId,
		variantsByVariantId,
		safeLockConfigurationProductsBySku,
		colorsBySafeLockConfigurationProductSku,
		selectedSafeLockConfigurationProduct,
		isLoading,
		taxRate,
		countryCode,
	} = reducerState

	const name = selectedSafeLockConfigurationProduct?.name ?? ""
	const selectedColorHexCode =
		variantsByVariantId[variantId]?.attributes?.colorConfigurationHexCode

	const sortedLockConfigurationProductSku = (
		Object?.keys(safeLockConfigurationProductsBySku) ?? []
	)
		.map((lockConfigurationProductSku) => {
			const lockConfigurationProduct =
				safeLockConfigurationProductsBySku?.[lockConfigurationProductSku]

			const { pricesByCountryCode } = lockConfigurationProduct
			const netPriceCents = pricesByCountryCode[countryCode] ?? 0

			return {
				sku: lockConfigurationProductSku,
				netPriceCents,
			}
		})
		.sort((a, b) => a.netPriceCents - b.netPriceCents)
		.map(({ sku }) => sku)

	const setLock = (variantId: number) => {
		setSafeVariant(dispatch, reducerState, variantId)
	}

	return (
		<div>
			<Box>
				<Box mb={1} style={{ fontWeight: "bold" }}>
					{t("chooseLockVariant")}
				</Box>
				<StyledToggleButtonGroup
					size="small"
					exclusive
					aria-label="text alignment"
					style={{ flexFlow: "wrap" }}
				>
					{sortedLockConfigurationProductSku.map((lockConfigurationProductSku) => {
						const variantId = getVariantIdWithMatchingColorIfPossible(
							colorsBySafeLockConfigurationProductSku,
							lockConfigurationProductSku,
							selectedColorHexCode,
						)
						if (variantId === 1) return null

						const isSelected =
							lockConfigurationProductSku === selectedSafeLockConfigurationProduct.sku
						const lockConfigurationProduct =
							safeLockConfigurationProductsBySku?.[lockConfigurationProductSku]
						const { name, image, pricesByCountryCode, description } =
							lockConfigurationProduct
						const netPriceCents = pricesByCountryCode[countryCode] ?? 0
						const grossPriceCents = Math.round(netToGross(netPriceCents, taxRate))

						return (
							<ArrowTooltip
								key={lockConfigurationProductSku}
								title={
									<LockToolTip
										image={image}
										name={name}
										description={description}
									/>
								}
								disableFocusListener
								disableTouchListener
							>
								<ToggleButton
									value={name}
									aria-label="left aligned"
									className={classes.toggleButton}
									style={{
										backgroundColor: isSelected ? "#E7EEF0" : null,
										border: isSelected
											? "1px solid #7abfac"
											: "1px solid lightgrey",
										position: "relative",
									}}
									disabled={isLoading}
									onClick={() => setLock(variantId)}
								>
									<Box mt={1}>
										{image && (
											<div
												style={{
													position: "absolute",
													top: "5px",
													left: "15%",
												}}
											>
												<Image
													src={image}
													alt={name}
													width={55}
													height={55}
													style={{
														maxWidth: "100%",
														height: "auto",
														objectFit: "contain",
													}}
												/>
											</div>
										)}
									</Box>
									{isSelected && (
										<div
											style={{
												position: "absolute",
												top: 0,
												right: 0,
												marginTop: "-8px",
												marginRight: "-5px",
											}}
										>
											<Icon
												path={mdiCheckCircle}
												size={0.9}
												color="#7abfac"
												title="Selected Lock"
											/>
										</div>
									)}
									<div
										style={{
											position: "absolute",
											bottom: 0,
											width: "100%",
										}}
									>
										+ <Price priceInEuroCents={grossPriceCents} />
									</div>
								</ToggleButton>
							</ArrowTooltip>
						)
					})}
				</StyledToggleButtonGroup>
			</Box>
			{name && (
				<Box mb={1}>
					{t("selected")} {name}
				</Box>
			)}
		</div>
	)
}

interface Lock {
	name: string
	image: string
	description: string
}

interface Props {
	dispatch: DispatchProductDetailPageReducer
	reducerState: ProductDetailPageReducerState
}

export default LockConfiguration
